// recurringReminderSetting.js — Phase 208 / card 208-B4.
//
// The GLOBAL opt-in switch for recurring-transaction reminders.
//
// WHY A SEPARATE MODULE
// ----------------------
// The setting itself needs no schema: `settings` is a generic key-value
// table on both sides —
//   server: `server/db.js` — PRIMARY KEY (user_id, key)
//   local:  Dexie `settings` table — (local_uuid, key, deleted)
// — so `ds.getSettings()` already returns whatever key was written, and
// `writes.setSetting()` already pushes it to the server in the background.
//
// What this module adds is the *semantics*, in one place, so the three
// consumers (card 208-A2 settings toggle, card 208-A3 dashboard effect,
// and the tests) cannot disagree:
//
//   - the key name            → RECURRING_REMINDER_SETTING_KEY
//   - the default             → OFF when the key is absent
//   - the value encoding      → the strings 'true' / 'false'
//   - the read helper         → isRecurringReminderEnabled(settings)
//   - the write helper        → setRecurringReminderEnabled(enabled)
//
// WHY THE DEFAULT IS "OFF"
// ------------------------
// `recurring_reminder_enabled` is the MASTER switch for the whole Phase
// 208 feature (phase spec §5.4). Even a rule with `reminder_enabled = 1`
// must not nudge the user until the user has explicitly opted in. An
// absent key therefore means OFF — never "unknown, assume on".
//
// WHY STRINGS AND NOT BOOLEANS
// ----------------------------
// The settings table stores TEXT. `notification_on_grace` and
// `weekly_recap_enabled` (the two existing notification opt-ins) both
// use the string 'true' / 'false', and the server's PUT validator for
// `notification_on_grace` rejects anything else. This module keeps the
// same encoding so the two toggles behave identically.
//
// LOCAL-FIRST (AGENTS.md rule 4)
// ------------------------------
// The read helper takes an already-loaded settings object — it never
// fetches. The caller does `const s = await ds.getSettings()` and passes
// `s` in. The UI must never depend on a server round-trip to read this.
//
// NOTIFICATION PERMISSION IS NOT THIS SETTING
// --------------------------------------------
// This toggle says "the user WANTS recurring reminders". Whether the
// BROWSER will show them is a separate question, answered by
// `Notification.permission` (and `notifications.ensurePermission()`).
// The settings UI asks for the permission before writing this key
// (card 208-A2); the firing path checks the permission again before
// notifying (card 208-B2). Keeping them separate means a revoked
// permission never silently rewrites the user's stated preference.
//
// THIS MODULE IS PURE
// -------------------
// No imports, no I/O, no Dexie. That keeps it trivially testable and
// keeps it out of the `dataService.js` ↔ `recurringReminderSetting.js`
// import cycle. The impure half (read + write against the local
// settings table) lives on `dataService.js` as
// `data.getRecurringReminderEnabled()` and
// `writes.setRecurringReminderEnabled(enabled)`.

/** The settings key. Matches the phase spec §4.3 and the server KV table. */
export const RECURRING_REMINDER_SETTING_KEY = 'recurring_reminder_enabled';

/** The value stored when the user has the feature ON. */
export const RECURRING_REMINDER_ON = 'true';

/** The value stored when the user has the feature OFF. */
export const RECURRING_REMINDER_OFF = 'false';

/**
 * Is the global recurring-reminder switch ON?
 *
 * Treats an absent / undefined / non-'true' value as OFF. Never throws:
 * a malformed settings object must not break the dashboard.
 *
 * @param {Object|null|undefined} settings - the object from `ds.getSettings()`
 * @returns {boolean}
 */
export function isRecurringReminderEnabled(settings) {
  if (!settings || typeof settings !== 'object') return false;
  return settings[RECURRING_REMINDER_SETTING_KEY] === RECURRING_REMINDER_ON;
}

/**
 * The canonical stored value for a boolean.
 *
 * STRICT on purpose. Only `true` and the string `'true'` map to
 * `'true'`; EVERYTHING else maps to `'false'`.
 *
 * Why not `enabled ? ON : OFF`: that is truthy-based, so the string
 * `'false'` would map to `'true'` — and `'false'` is exactly what this
 * setting stores when the feature is off. A caller that round-trips
 * the stored value back through this helper (e.g.
 * `setRecurringReminderEnabled(settings.recurring_reminder_enabled)`)
 * would then silently switch the MASTER switch ON. The reader is
 * strict (`=== 'true'`); the writer has to be just as strict, or the
 * two halves of this module disagree about what "on" means — which is
 * the whole reason this module exists.
 *
 * @param {boolean|string} enabled
 * @returns {'true'|'false'}
 */
export function recurringReminderEnabledValue(enabled) {
  return (enabled === true || enabled === RECURRING_REMINDER_ON)
    ? RECURRING_REMINDER_ON
    : RECURRING_REMINDER_OFF;
}

export default {
  RECURRING_REMINDER_SETTING_KEY,
  RECURRING_REMINDER_ON,
  RECURRING_REMINDER_OFF,
  isRecurringReminderEnabled,
  recurringReminderEnabledValue,
};
